import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';
import CampaignModel, {
  CampaignStateActions,
  ObjectRelationshipTriggerType,
  ObjectTriggerType,
} from 'cio/models/campaign';
import CustomerService from 'cio/services/customer';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { task } from 'ember-concurrency';
import CampaignNavStepController from 'cio/controllers/campaign-nav-step';
import AuthService from 'cio/services/auth';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { SerializedLegacySegmentNegatableFilter } from 'cio/types/conditions/legacy-filter';
import { getAudienceSummaryText } from 'cio/utils/campaigns/sumaries';

const DEFAULT_MODAL_COPY = {
  buttonText: 'Start Campaign',
  pendingTitle: 'Starting campaign…',
  successTitle: 'Campaign started!',
  successText: 'Show campaign overview',
  failureTitle: 'Oops! Failed to start',
};

const API_TRIGGERED_MODAL_COPY = {
  buttonText: 'Activate Broadcast',
  pendingTitle: 'Activating Broadcast…',
  successTitle: 'Broadcast activated!',
  successText: 'Broadcast overview',
  failureTitle: 'Oops! Failed to activate',
};

export default class IndexController extends CampaignNavStepController {
  @service customer!: CustomerService;
  @service store!: DS.Store;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service subscriptionCenter!: SubscriptionCenterService;

  queryParams = [
    {
      campaignStarted: 'campaign_started',
    },
  ];

  // This query param is used to issue a page view event when the campaign
  // successfully starts, which is then used for in-app message triggers
  @tracked campaignStarted = false;

  messagePendingImage = new URL('cio/assets/images/message-pending.svg', import.meta.url).toString();
  messageSentImage = new URL('cio/assets/images/message-sent.svg', import.meta.url).toString();
  messageFailureImage = new URL('cio/assets/images/message-failure.svg', import.meta.url).toString();

  declare model: CampaignModel;

  // As of right now, we don't have a Settings section to display data in
  readonly reviewSections: Record<string, any>[] = [];

  readonly ObjectTriggerTypes = ObjectTriggerType;
  readonly RelationshipTriggerTypes = ObjectRelationshipTriggerType;

  @computed('model.isApiTriggered')
  get startModalCopy() {
    return this.model.isApiTriggered ? API_TRIGGERED_MODAL_COPY : DEFAULT_MODAL_COPY;
  }

  @computed(
    'model.{allReady,backfill,isSegmentTriggered,hasDeletedSubscriptionTopic,hasActionsUsingDeletedSubscriptionTopic}',
  )
  get canStart() {
    let { model } = this;
    let eventTriggeredReqs = !model.isSegmentTriggered && model.allReady;
    let segmentTriggeredReqs = model.isSegmentTriggered && model.allReady && !isNone(model.backfill);
    return (
      (eventTriggeredReqs || segmentTriggeredReqs) &&
      !model.hasDeletedSubscriptionTopic &&
      !model.hasActionsUsingDeletedSubscriptionTopic
    );
  }

  @computed('model.allReady', 'customer.fetchRecipientCount.last.value')
  get canSelectForceMatch() {
    return this.model.allReady && this.customer.fetchRecipientCount.last?.value !== 0;
  }

  @computed('customer.fetchRecipientCount.last.value', 'model.{anchors,filters,recipientsFilter}')
  get hasRecipients() {
    let {
      model: { anchors, filters, recipientsFilter },
    } = this;

    if (isEmpty(anchors) && isEmpty(filters) && isEmpty(recipientsFilter)) {
      return false;
    }

    return this.customer.fetchRecipientCount.last?.value !== 0;
  }

  @tracked untranslatedMultiLangMessagingActionIds: string[] = [];

  @computed('model.hasMultiLanguageBranchAction')
  get campaignHasMultiLangMessages() {
    return this.model.hasMultiLanguageBranchAction;
  }

  get campaignHasUntranslatedMultiLangMessages() {
    return this.untranslatedMultiLangMessagingActionIds.length > 0;
  }

  get untranslatedMultiLangMessagingActionsInfo() {
    this.model.ensureParsedTree();
    let actionsInfo: object[] = [];
    for (let actionId of this.untranslatedMultiLangMessagingActionIds) {
      // We want to use the default action's name because when the action gets renamed, other translation
      // variation actions are updated on the backend to be in sync, and their states can be out of sync.
      let defaultAction = this.model.getConsolidatedActionsGroupDefaultActionByChildActionId(actionId);
      if (defaultAction != null) {
        actionsInfo.push({
          id: actionId,
          name: defaultAction.name,
          icon: defaultAction.info.icon,
          language: this.model.getConsolidatedBranchChildActionLanguage(actionId),
        });
      }
    }
    return actionsInfo;
  }

  get segmentIds(): (number | string)[] {
    let { isSegmentTriggered, anchors } = this.model;
    if (!isSegmentTriggered) {
      return [];
    }
    return anchors.flatMap((anchor: SerializedLegacySegmentNegatableFilter) => anchor.segments ?? []);
  }

  get showRelationshipSegmentHistoricalDataTriggerWarning() {
    let { isSegmentTriggered, backfill } = this.model;
    let someSegmentHasRelationshipCondition = this.segmentIds.some(
      (id) => this.store.peekRecord('short-segment', id)?.hasRelationshipConditions,
    );
    return isSegmentTriggered && backfill === false && someSegmentHasRelationshipCondition;
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  get audienceSummary() {
    return getAudienceSummaryText(this.model, this.model.objectTypeForCampaign);
  }

  fetchSegments = task({ restartable: true }, async () => {
    let { segmentIds } = this;
    if (segmentIds.length > 0 && this.store.peekAll('short-segment').length === 0) {
      await this.store.findAll('short-segment');
    }
  });

  fetchUntranslatedMultiLangMessagingActionIds = task({ drop: true }, async () => {
    this.untranslatedMultiLangMessagingActionIds = [];
    if (!this.campaignHasMultiLangMessages) {
      return;
    }

    try {
      let path = `campaigns/${this.model.id}/untranslated_actions`;
      let response = await this.api.request(path);
      this.untranslatedMultiLangMessagingActionIds = response ?? [];
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  async _updateSetupGuideSendCampaignStep(campaign: CampaignModel) {
    // It's not possible to create and send API-triggered broadcasts through setup guide
    // so ignore them when making any changes to state of the setup guide:
    if (campaign.isApiTriggered) {
      return;
    }

    let setupGuide = await this.store.findRecord('setup-guide', 'current');

    if (setupGuide.completedSteps['send-campaign']) {
      return;
    }

    await setupGuide.assignFirstMessage(campaign);
  }

  @action
  async startCampaign() {
    let { model } = this;

    await model.changeState({
      stateAction: CampaignStateActions.Start,
      backfill: model.isDateTriggered ? true : model.backfill,
    });

    // update setup guide "send campaign" step if not completed already
    // non-critical operation: launched in the background
    this._updateSetupGuideSendCampaignStep(model);

    // trigger event on satismeter to display survey on why no goal was created
    if (!model.conversion && 'satismeter' in window) {
      (window as any).satismeter('track', { event: 'activated_campaign_with_no_goal' });
    }

    this.campaignStarted = true;
  }
}
